import classNames from 'clsx';
import { isNil } from 'lodash';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import StarsButton from '@swf/components/StarsButton/StarsButton';
import { Site } from '@swf/core/enums';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { CasinoRace } from '@swf/core/types/CasinoRaces';
import type { TrackedTranslation } from '@swf/core/types/i18n';
import { isFinished, isRunning } from '@swf/core/utils/casino';

import {
  TXTCLI_Play,
  TXTCLI_Register__toTournament,
  TXTCLI_Registered,
  TXTWCL_Join
} from './RacePlayButton.messages.json';
import styles from './RacePlayButton.scss';

export interface Props {
  race: CasinoRace;
  widgetId?: string;
  path?: string;
  size?: 'small' | 'large';
  isModal?: boolean;
}

const RacePlayButton: React.FC<Props> = ({ race, widgetId, path, size = 'large', isModal }) => {
  const { gameInfo, id, multiGameList, raceTypes, status, tncVersion, userRaceStanding } = race;
  const hasMultiGames = multiGameList && multiGameList.length > 1;
  const running = isRunning(status);
  const finished = isFinished(status, !userRaceStanding);
  const userRegistered = !isNil(userRaceStanding);
  const { casino, modal } = useDispatch<Dispatch>();
  const siteId = useSelector(select.site.getId);
  let btnLabel: TrackedTranslation = TXTCLI_Register__toTournament;

  if (running) {
    btnLabel = siteId === Site.GR ? TXTWCL_Join : TXTCLI_Play;
  } else if (userRaceStanding) {
    btnLabel = TXTCLI_Registered;
  }

  const openGameSelector = useCallback(() => {
    modal.replace([
      {
        path: '/casino/race-game-selector',
        locationState: { widgetId, path }
      }
    ]);
  }, [modal, widgetId, path]);

  const launchRace = useCallback(() => {
    if (running && hasMultiGames) {
      openGameSelector();
    } else {
      casino.race({ game: gameInfo, raceId: id, raceTypes, tncVersion, join: !running });
    }
    modal.close('/casino/race-info');
  }, [
    modal,
    running,
    casino,
    gameInfo,
    tncVersion,
    id,
    raceTypes,
    hasMultiGames,
    openGameSelector
  ]);

  return (
    <StarsButton
      className={classNames(userRegistered && !running && styles.register)}
      disabled={finished || (!running && userRegistered)}
      size={size}
      block
      onClick={launchRace}
      translation={btnLabel}
    />
  );
};

export default RacePlayButton;
